import { memo, useCallback, useMemo } from 'react';

import { ChakraFlex, ChakraBox, SkeletonLoader } from '@equip.health/ui';
import SelectableUserAvatarBlock from '../SelectableUserAvatarBlock';
import Tabs from '../Tabs';
import { ProviderAssignmentSelectionTab } from '../constants';
import { sortProvidersByAvailability } from '../utils';

const TOP_LEVEL_ID = 'provider-only-selection';

interface ProviderOnlySelectionProps {
  availableSlots: ProviderAppointmentSlot[];
  defaultTab?: ProviderAssignmentSelectionTab;
  isLoading: boolean;
  onSelect: (providerId: string) => void;
  onTabChange?: (tab: ProviderAssignmentSelectionTab) => void;
  selectedProviderId?: string;
  tieredProviderMap: Record<
    ProviderAssignmentSelectionTab,
    Record<string, GetProviderByIdResponse>
  >;
  topNProviderIds: Set<string>;
}

const ProviderOnlySelection = ({
  availableSlots,
  defaultTab,
  isLoading,
  onSelect,
  onTabChange,
  selectedProviderId,
  tieredProviderMap,
  topNProviderIds,
}: ProviderOnlySelectionProps) => {
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, providerId: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(providerId);
      }
    },
    [onSelect],
  );

  const sortedTieredProviderMap = useMemo(() => {
    return Object.fromEntries(
      Object.entries(tieredProviderMap).map(([tab, providers]) => [
        tab,
        sortProvidersByAvailability(
          Object.values(providers),
          availableSlots,
          topNProviderIds,
        ),
      ]),
    ) as Record<ProviderAssignmentSelectionTab, GetProviderByIdResponse[]>;
  }, [tieredProviderMap, availableSlots, topNProviderIds]);

  const renderProviderList = useCallback(
    (tab: ProviderAssignmentSelectionTab) => {
      const sortedProviders = sortedTieredProviderMap[tab];

      return (
        <SkeletonLoader id={TOP_LEVEL_ID} isLoaded={!isLoading}>
          <ChakraFlex direction="column" gap="12px">
            {sortedProviders.map((provider) => (
            <ChakraBox
              _hover={{
                backgroundColor: 'primary.10',
                borderColor: 'primary.100',
              }}
              aria-label={`Select ${provider.chosenName ?? provider.firstName} ${provider.lastName}`}
              aria-pressed={selectedProviderId === provider.userId}
              backgroundColor={
                selectedProviderId === provider.userId ? 'primary.10' : 'white'
              }
              borderColor={
                selectedProviderId === provider.userId
                  ? 'primary.100'
                  : 'neutral.line'
              }
              borderRadius="12px"
              borderWidth="1px"
              cursor="pointer"
              key={provider.userId}
              onClick={() => onSelect(provider.userId)}
              onKeyDown={(e) => onKeyDown(e, provider.userId)}
              role="button"
              tabIndex={0}
            >
              <SelectableUserAvatarBlock
                id={TOP_LEVEL_ID}
                isSelected={selectedProviderId === provider.userId}
                user={provider}
              />
            </ChakraBox>
          ))}
        </ChakraFlex>
      </SkeletonLoader>
      );
    },
    [isLoading, onKeyDown, onSelect, selectedProviderId, sortedTieredProviderMap],
  );

  return (
    <Tabs
      defaultTab={defaultTab}
      id={TOP_LEVEL_ID}
      onTabChange={onTabChange}
      renderTabContent={renderProviderList}
      tieredProviderMap={tieredProviderMap}
    />
  );
};

export default memo(ProviderOnlySelection);
